import { useSyncExternalStore } from 'react';

// Drafts per channel and thread, kept outside the screen so they outlive it:
// the text you were writing and the files uploading for it carry on while you
// look at something else. Text and finished uploads also survive a reload;
// an upload still in flight can't (the browser drops the file).

export interface DraftUpload {
  key: string;
  name: string;
  size: number;
  progress: number;
  done?: { type: 'file' | 'vault_link'; id: string };
  error?: string;
}

export interface Draft {
  text: string;
  uploads: DraftUpload[];
  /** Where it's going, for showing elsewhere: "#general" or "thread in #general". */
  where: string;
  /** Where to come back to it. */
  href: string;
}

export type DraftPlace = Pick<Draft, 'where' | 'href'>;

const STORAGE_KEY = 'hive.comms.drafts';
let drafts: Readonly<Record<string, Draft>> = load();
const listeners = new Set<() => void>();

export function draftKey(channelId: string, parentId: string | null = null): string {
  return parentId ? `${channelId}:${parentId}` : channelId;
}

function load(): Record<string, Draft> {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, Draft>;
    const out: Record<string, Draft> = {};
    for (const [key, d] of Object.entries(raw)) {
      const uploads = (d.uploads ?? []).filter((u) => u.done);
      if (d.text || uploads.length) out[key] = { ...d, uploads };
    }
    return out;
  } catch {
    return {};
  }
}

function save() {
  try {
    const kept: Record<string, Draft> = {};
    for (const [key, d] of Object.entries(drafts)) {
      const uploads = d.uploads.filter((u) => u.done);
      if (d.text || uploads.length) kept[key] = { ...d, uploads };
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
  } catch {
    // No storage (private window): drafts still last until the window closes.
  }
}

function update(key: string, place: DraftPlace | null, change: (d: Draft) => Draft) {
  const before = drafts[key] ?? (place ? { text: '', uploads: [], ...place } : null);
  if (!before) return;
  const after = change(place ? { ...before, ...place } : before);
  const next = { ...drafts };
  if (!after.text && after.uploads.length === 0) delete next[key];
  else next[key] = after;
  drafts = next;
  save();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useDraft(key: string): Draft | undefined {
  return useSyncExternalStore(subscribe, () => drafts[key]);
}

export function useAllDrafts(): Readonly<Record<string, Draft>> {
  return useSyncExternalStore(subscribe, () => drafts);
}

export function getDraft(key: string): Draft | undefined {
  return drafts[key];
}

export function setDraftText(key: string, place: DraftPlace, text: string) {
  update(key, place, (d) => ({ ...d, text }));
}

/** Starts uploading files for a draft; they keep going if the screen closes. */
export function addDraftUploads(
  key: string,
  place: DraftPlace,
  files: readonly File[],
  run: (file: File, onProgress: (fraction: number) => void) => Promise<{ type: 'file' | 'vault_link'; id: string }>,
) {
  for (const file of files) {
    const uploadKey = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
    const patch = (fields: Partial<DraftUpload>) =>
      update(key, null, (d) => ({
        ...d,
        uploads: d.uploads.map((u) => (u.key === uploadKey ? { ...u, ...fields } : u)),
      }));
    update(key, place, (d) => ({
      ...d,
      uploads: [...d.uploads, { key: uploadKey, name: file.name, size: file.size, progress: 0 }],
    }));
    run(file, (progress) => patch({ progress })).then(
      (done) => patch({ done, progress: 1 }),
      (e: unknown) => patch({ error: e instanceof Error ? e.message : 'Upload failed.' }),
    );
  }
}

export function removeDraftUpload(key: string, uploadKey: string) {
  update(key, null, (d) => ({ ...d, uploads: d.uploads.filter((u) => u.key !== uploadKey) }));
}

export function clearDraft(key: string) {
  update(key, null, () => ({ text: '', uploads: [], where: '', href: '' }));
}

export const isUploading = (d: Draft | undefined) => !!d?.uploads.some((u) => !u.done && !u.error);

/** Drops every draft (signing out, and tests). */
export function clearAllDrafts() {
  drafts = {};
  save();
  for (const listener of listeners) listener();
}

/** Drops a channel's drafts, its threads' included (when the channel is deleted). */
export function clearChannelDrafts(channelId: string) {
  for (const key of Object.keys(drafts)) {
    if (key === channelId || key.startsWith(`${channelId}:`)) clearDraft(key);
  }
}
